import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { sportTypeIds, utrTypeIds, teamPaymentTypeIds } from '../../../utils/eventConstants';

import {
    AdditionalSettings,
    AdultTeamTennisMatch,
    AwardCombinedDivisionDrawMedalToggle,
    DisplayMedalAwardsToggle,
    Divisions,
    DivisionDrawAssociationToggle,
    DualMatch,
    EntryFee,
    EventAllottedMatchTimes,
    EventDetails,
    EventLocation,
    EventName,
    EventOrganizers,
    EventPayment,
    FacilityLocations,
    HealthAndSafety,
    PreventDivisionDateRegistration,
    PreventProAmCategoryRegistration,
    PrizeMoney,
    Schedule,
    SelectEventType,
    SelectHSEventType,
    SelectVerified,
    SelfWithdraw,
    USAPVerificationType,
    RemoveWithdrawnPlayersFromStandingsToggle,
} from './Fields';

import { isPersonalClub, isHighSchoolClub, ClubTierTypes, isClubOwner } from '../../../utils/clubs';
import { EventTypes } from '../../../utils/events';

import { clubs } from '../../../../constants';
import PreventSameDateDivisionPopup from '../Popups/PreventSameDateDivisionPopup';
import PreventProAmDivisionCategoryPopup from '../Popups/PreventProAmDivisionCategoryPopup';
import AddSponsorTier from './Fields/AddSponsorTier';
import SwitchDivisionDate from './Fields/SwitchDivisionDate';
import ChangePartnerDate from './Fields/ChangePartnerDate';
import AllowMultiRegistrations from './Fields/AllowMultiRegistrations';
import EnablePreRegToggle from './Fields/EnablePreRegToggle';
import { useStatsig } from '../../hooks/useStatsig';
import { clubShape, tierShape } from '../../../propTypes';

function StepFields(props) {
    const {
        formApi,
        club,
        defaultValues,
        currentStep,
        showTip,
        showPaymentSetup,
        stripeUrl,
        activePaymentsEnabled,
        isDemoEvent,
        openModal,
        closeCreatePage,
        applyForVerified,
        verificationApplied,
        onBack,
        organizers,
        setOrganizers,
        onHSEventTypeChange,
        onEventTypeChange,
        onDivisionFeeChange,
        onVerifiedChange,
        PopUpTypes,
        editing,
        isOwner,
        showUpgradeToPerformanceHsPopup,
        isRegionLocked,
        eventId,
        sportTypeId,
        isEventCreateForm,
        tiers,
        setTiers,
        remainingTiers,
        setRemainingTiers,
        hasDraws,
        registrationsCount,
    } = props;

    const auth = useSelector(({ auth }) => auth);
    const { checkGate, featureGates } = useStatsig();
    const [showPopup, setShowPopup] = useState(false);
    const { values } = formApi;

    const { clubDetails } = club;
    const clubIsHighSchool = isHighSchoolClub(clubDetails.tierType);
    const canUpgradeToPerformanceHs = isClubOwner(clubDetails);
    const hasEventNameAndLocation = !clubIsHighSchool ? !!(values.name && values.eventLocations[0]?.location) : true;
    const isMultiRegistrationEnabled = checkGate(featureGates.CAMPS_MULTI_REGISTRATIONS);
    const usapPreRegInvitesEnabled = checkGate(featureGates.USAP_PRE_REG_INVITES);
    const allottedMatchTimesEnabled = checkGate(featureGates.ALLOTTED_MATCH_TIMES);
    const isEventFacilitySelectionEnabled = checkGate(featureGates.EVENT_FACILITY_SELECTION);
    const showAllowMultiRegistrationToggle = values.eventType === EventTypes.CAMP && isMultiRegistrationEnabled;

    const showSelectVerified = () => {
        const isHighSchoolTournament = clubIsHighSchool && values?.hsEventType === EventTypes.TOURNAMENT;
        const canRunVerifiedEvents = !clubIsHighSchool || isHighSchoolTournament || canUpgradeToPerformanceHs;
        return hasEventNameAndLocation && values.eventType && canRunVerifiedEvents && clubDetails.tierType.verificationAllowed;
    };
    const disableSelectVerified = () => {
        return values.published ||
            (clubDetails?.tierType?.id === ClubTierTypes.HIGH_SCHOOL && values.hsEventType) ||
            (clubDetails?.tierType?.id === ClubTierTypes.PERFORMANCE_HIGH_SCHOOL && values.hsEventType === EventTypes.TOURNAMENT);
    };

    const showSelectIsConference = () => {
        return (hasEventNameAndLocation && values.eventType && values.eventType === EventTypes.DUAL_MATCH);
    };

    const isPrizeMoneyAllowed = () => {
        const isHsEventTypeSelected = values.hsEventType && values.hsEventType !== 1;
        const hsEventTypeAllowed = !clubIsHighSchool || (clubIsHighSchool && isHsEventTypeSelected);
        return hasEventNameAndLocation && values.eventType && !isPersonalClub(clubDetails.tierType) && hsEventTypeAllowed;
    };

    const showDivisions = () => {
        return hasEventNameAndLocation && values.eventType && (parseInt(values.$verified) === utrTypeIds.MYUTR || parseInt(values.$verified) === utrTypeIds.SANCTIONED);
    };

    const showSelectHSEventType = () => {
        return isHighSchoolClub(clubDetails.tierType);
    };

    const showSelectEventType = () => {
        return !clubIsHighSchool ||
            (clubIsHighSchool && (
                (values.hsEventType === 1 || values.hsEventType === 2)
            ));
    };

    const currency = values?.currency ? values.currency : clubDetails.currency;
    const isFlexLeagueEvent = values?.eventType === EventTypes.FLEX_LEAGUE;
    const isEntryFeeAllowed = (values) => {
        return !!clubDetails?.activePaymentProviderTypes && values.eventType && (parseInt(values.$verified) === utrTypeIds.MYUTR || parseInt(values.$verified) === utrTypeIds.SANCTIONED) && clubDetails?.tierType?.id !== ClubTierTypes.HIGH_SCHOOL && hasEventNameAndLocation;
    };
    const isUSAPToggleAllowed = (values) => {
        return parseInt(values.sportTypeId) === sportTypeIds.PICKLEBALL && values.eventType && hasEventNameAndLocation;
    };
    const divisionsDate = new Set();
    const updateEventHasSameDateDivisions = () => {
        const n = values?.eventDivisions.length;
        for (let i = 0; i < n; i += 1) {
            const div = values.eventDivisions[i];
            if (divisionsDate.has(div?.specificDivisionDateUtc)) { return true; }
            if (div?.specificDivisionDateEnabled) {
                divisionsDate.add(div?.specificDivisionDateUtc);
            }
        }
        return false;
    };
    const eventHasDivisionsWithSameDate = updateEventHasSameDateDivisions();
    const eventHasProAmConflicts = values?.hasProAmRegistrationConflict;
    const showPreventDivisionDateRegistration = (
        <PreventDivisionDateRegistration
            field="preventDivisionDateRegistrationConflicts"
            value={values.preventDivisionDateRegistrationConflicts}
            formApi={formApi}
            eventHasSameDateDivisions={eventHasDivisionsWithSameDate}
            setShowPopup={(value) => setShowPopup(value)}
        />
    );

    const showDateError = isEventCreateForm && !!values?.preventDivisionDateRegistrationConflicts && values?.eventDivisions.every(division => !division?.specificDivisionDateEnabled);
    const isRestrictProAndAmateurRegistrationEnabled = checkGate(featureGates.RESTRICT_PRO_AND_AMATEUR_REGISTRATION);
    const showPreventProAmCategoryRegistration = isRestrictProAndAmateurRegistrationEnabled
        ? (
            <PreventProAmCategoryRegistration
                field="preventProAmDivisionRegistrationConflicts"
                value={values.preventProAmDivisionRegistrationConflicts}
                formApi={formApi}
                eventHasProAmConflicts={eventHasProAmConflicts}
                setShowPopup={(value) => setShowPopup(value)}
            />
        ) : null;

    const isDrawDivisionAssociationEnabled = checkGate(featureGates.DRAW_DIVISION_ASSOCIATION);
    const isMedalWinnersDisplayEnabled = checkGate(featureGates.MEDAL_WINNERS_DISPLAY);
    const isPlayerAddPartnerFlowEnabled = checkGate(featureGates.PLAYER_ADD_PARTNER_FLOW);
    const isRemoveWithdrawnPlayersFromStandingsEnabled = checkGate(featureGates.REMOVE_WITHDRAWN_PLAYERS_FROM_STANDINGS);

    const showDisplayMedalAwardsToggle = isMedalWinnersDisplayEnabled && values.sportTypeId === sportTypeIds.PICKLEBALL ? (
        <DisplayMedalAwardsToggle
            field="awardDrawMedal"
            value={values.awardDrawMedal}
            formApi={formApi}
        />
    ) : null;

    const showDivisionDrawAssociationToggle = isDrawDivisionAssociationEnabled ? (
        <DivisionDrawAssociationToggle
            field="divisionDrawAssociation"
            value={values.divisionDrawAssociation}
            formApi={formApi}
            disabled={hasDraws}
        />
    ) : null;

    const showAwardCombinedDivisionDrawMedalToggle = isDrawDivisionAssociationEnabled && values?.awardDrawMedal && values?.divisionDrawAssociation ? (
        <AwardCombinedDivisionDrawMedalToggle
            field="awardCombinedDivisionDrawMedal"
            value={values.awardCombinedDivisionDrawMedal}
            formApi={formApi}
        />
    ) : null;

    const EntryFeeComponent = (
        <EntryFee
            field="hasEntryFee"
            value={values.hasEntryFee}
            formApi={formApi}
            eventEntryFeeField="entryFee"
            currency={currency}
            allowOffPlatformPayments={clubDetails.allowOffPlatformPayments}
            minimumEntryFee={clubDetails.minimumEntryFee}
            formattedMinimumEntryFee={clubDetails.formattedMinimumEntryFee}
            playersRegistered={values.registeredPlayers > 0}
        />
    );

    const USAPVerificationTypeComponent = (
        <USAPVerificationType
            field="hasUsapVerificationTypeId"
            value={values.hasUsapVerificationTypeId}
            formApi={formApi}
            usapVerificationTypeField="usapVerificationTypeId"
            playersRegistered={values.registeredPlayers > 0}
        />
    );

    const showRemoveWithdrawnPlayersFromStandingsToggle = isRemoveWithdrawnPlayersFromStandingsEnabled && values.sportTypeId === sportTypeIds.PICKLEBALL ? (
        <RemoveWithdrawnPlayersFromStandingsToggle
            field="removeWithdrawnPlayersFromStandings"
            value={values.removeWithdrawnPlayersFromStandings}
            formApi={formApi}
        />
    ) : null;

    const showEntryFee = isEntryFeeAllowed(values) ? EntryFeeComponent : null;
    const showPopupComponent = showPopup && values?.registeredPlayers > 0 ? <PreventSameDateDivisionPopup setShowPopup={(value) => setShowPopup(value)} /> : null;
    const showProAmPopupComponent = showPopup && values?.hasProAmRegistrationConflict ? <PreventProAmDivisionCategoryPopup setShowPopup={(value) => setShowPopup(value)} /> : null;
    const showEventAllotedMatchTimesSection = allottedMatchTimesEnabled && isEntryFeeAllowed(values);
    const showUSAPVerificationType = isUSAPToggleAllowed(values) ? USAPVerificationTypeComponent : null;
    const hasDivisionWithTeamPayment = values?.eventDivisions?.some(div => parseInt(div.teamPaymentTypeId) === teamPaymentTypeIds.PAY_AS_A_TEAM);

    const getStepFields = () => {
        const showDivisionsElement = () => {
            return (
                <Divisions
                    field="eventDivisions"
                    formApi={formApi}
                    isFlexLeagueEvent={isFlexLeagueEvent}
                    allowOffPlatformPayments={clubDetails.allowOffPlatformPayments}
                    currency={currency}
                    editing={editing}
                    registeredPlayers={values.registeredPlayers}
                    clubTierType={clubDetails.tierType}
                    isDemoEvent={isDemoEvent}
                    paymentsEnabled={clubDetails.paymentsEnabled}
                    userIsPowered={auth?.user?.isPowered}
                    showTip={showTip}
                    onChange={onDivisionFeeChange}
                    countryCode={clubDetails?.location?.countryCode2}
                    paymentProviderSetupType={clubDetails?.paymentProviderSetupType?.id}
                    minimumEntryFee={clubDetails.minimumEntryFee}
                    formattedMinimumEntryFee={clubDetails.formattedMinimumEntryFee}
                    allowZeroEntryFee={clubDetails.allowZeroEntryFee}
                    minimumOrZeroEntryFeeErrorMessage={clubDetails.minimumOrZeroEntryFeeErrorMessage}
                    minimumEntryFeeErrorMessage={clubDetails.minimumEntryFeeErrorMessage}
                    verificationFee={clubDetails.verificationFee}
                    tmsPercentFee={clubDetails.tmsPercentFee}
                    sportTypeId={sportTypeId}
                    showDateError={showDateError}
                    eventTypeId={values.eventType}
                />
            );
        };

        const defaultTournamentsAndDivisionsElement = (showPrizeMoneyAllowed = false) => {
            return (
                <>
                    {showSelectVerified() && (
                        <SelectVerified
                            field="$verified"
                            value={values.$verified}
                            user={auth.user}
                            formApi={formApi}
                            verifiedAllowed={clubDetails ? clubDetails.sanctioned : false}
                            verifiedPending={clubDetails?.verificationApplicationStatus === clubs.submittedForApproval}
                            applyForVerified={applyForVerified}
                            verificationApplied={verificationApplied}
                            showTip={showTip}
                            clubTierType={clubDetails.tierType}
                            disabled={disableSelectVerified()}
                            onChange={onVerifiedChange}
                            openModal={openModal}
                            editing={editing}
                            canUpgradeToPerformanceHs={canUpgradeToPerformanceHs}
                            showUpgradeToPerformanceHsPopup={showUpgradeToPerformanceHsPopup}
                        />
                    )}
                    {showUSAPVerificationType}
                    {showPrizeMoneyAllowed && (
                        <PrizeMoney
                            field="hasPrizeMoney"
                            value={values.hasPrizeMoney}
                            formApi={formApi}
                            prizeMoneyField="prizeMoney"
                            prizeMoneyValue={values.prizeMoney}
                            currency={currency}
                            allowOffPlatformPayments={clubDetails.allowOffPlatformPayments}
                            auth={auth}
                        />
                    )}
                    {values.eventType && usapPreRegInvitesEnabled && (
                        <EnablePreRegToggle
                            field="preRegAndEligibleListEnabled"
                            formApi={formApi}
                        />
                    )}
                    {values.eventType && hasEventNameAndLocation && (
                        <Schedule
                            field="eventSchedule"
                            formApi={formApi}
                            clubSettings={clubDetails?.clubSettings}
                            isHighSchoolClub={clubIsHighSchool}
                            editing={editing}
                        />
                    )}
                    {showEntryFee}
                    {showAllowMultiRegistrationToggle && (
                        <AllowMultiRegistrations formApi={formApi} />
                    )}
                    {values.eventType && values.eventSchedule && hasEventNameAndLocation && (
                        <SelfWithdraw
                            formApi={formApi}
                        />
                    )}
                    {values.eventType && values.eventSchedule && (
                        <SwitchDivisionDate
                            formApi={formApi}
                        />
                    )}
                    {isPlayerAddPartnerFlowEnabled && values.eventType && values.eventSchedule && (
                        <ChangePartnerDate
                            formApi={formApi}
                            disabled={(hasDivisionWithTeamPayment && !!registrationsCount)}
                            hasDivisionWithTeamPayment={hasDivisionWithTeamPayment}
                        />
                    )}
                </>
            );
        };

        const renderStepOneByEventType = (eventType) => {
            switch (eventType) {
                case EventTypes.TOURNAMENT:
                    return defaultTournamentsAndDivisionsElement(isPrizeMoneyAllowed());
                case EventTypes.DUAL_MATCH:
                    return (
                        <DualMatch
                            formApi={formApi}
                            editing={editing}
                            showTip={showTip}
                            clubSubType={clubDetails.clubSubType}
                            clubSchoolProfile={clubDetails.schoolProfile}
                            clubTierType={clubDetails.tierType}
                            defaultValues={defaultValues}
                            closeCreatePage={closeCreatePage}
                            lastEventIds={clubDetails.lastEventIds}
                            clubId={clubDetails.id}
                            showSelectIsConference={showSelectIsConference()}
                            clubLocation={clubDetails.location}
                        />
                    );
                case EventTypes.TEAM_MATCH:
                    return (
                        <AdultTeamTennisMatch
                            formApi={formApi}
                            editing={editing}
                            clubSubType={clubDetails.clubSubType}
                            clubDetails={clubDetails}
                            clubTierType={clubDetails.tierType}
                            defaultValues={defaultValues}
                            closeCreatePage={closeCreatePage}
                            lastEventIds={clubDetails.lastEventIds}
                            clubId={clubDetails.id}
                            showSelectIsStandings={true}
                        />
                    );
                default:
                    return defaultTournamentsAndDivisionsElement();
            }
        };

        switch (currentStep) {
            case 1:
                return renderStepOneByEventType(values.eventType);
            case 2:
                return (
                    <>
                        {showPopupComponent}
                        {showProAmPopupComponent}
                        {showPreventDivisionDateRegistration}
                        {showPreventProAmCategoryRegistration}
                        {showDisplayMedalAwardsToggle}
                        {showDivisionDrawAssociationToggle}
                        {showAwardCombinedDivisionDrawMedalToggle}
                        {(showEventAllotedMatchTimesSection && (
                            <EventAllottedMatchTimes
                                field="hasEventAllottedMatchTimes"
                                value={values.hasEventScoringFormats}
                                formApi={formApi}
                                eventAllottedMatchTimesField="matchTimes"
                                eventAllottedMatchTimesValue={values.matchTimes}
                                sportTypeId={sportTypeId}
                                auth={auth}
                            />
                        ))}
                        {showRemoveWithdrawnPlayersFromStandingsToggle}
                        {showDivisions() && showDivisionsElement()}
                    </>
                );

            case 3:
                return (
                    <>
                        {clubIsHighSchool && (
                            <>
                                <EventName
                                    field="name"
                                    formApi={formApi}
                                    clubTierType={clubDetails.tierType}
                                />
                                <EventLocation
                                    field="eventLocations"
                                    formApi={formApi}
                                    isEditing={editing}
                                    isRegionLocked={isRegionLocked}
                                    isHighSchoolClub={clubIsHighSchool}
                                />
                            </>
                        )}
                        <EventDetails
                            field="details"
                            formApi={formApi}
                            clubTierType={clubDetails.tierType}
                            sportTypeId={sportTypeId}
                        />
                        <EventOrganizers
                            formApi={formApi}
                            organizers={organizers}
                            clubOrganizers={clubDetails.clubOrganizers}
                            setOrganizers={setOrganizers}
                            PopUpTypes={PopUpTypes}
                        />
                        {isEventFacilitySelectionEnabled && (
                            <FacilityLocations
                                formApi={formApi}
                                field="facilities"
                                clubId={clubDetails.id}
                                eventId={eventId}
                                eventFacilities={values.eventFacilities}
                            />
                        )}
                        <AdditionalSettings
                            formApi={formApi}
                            isFlexLeagueEvent={isFlexLeagueEvent}
                            clubTierType={clubDetails.tierType}
                            clubTmsSmsFee={clubDetails.tmsSMSFee}
                            showClubJoin={clubDetails?.tierType?.id !== ClubTierTypes.TOURNAMENT}
                            clubLocation={clubDetails?.location}
                            playersRegistered={values.registeredPlayers > 0}
                            sportTypeId={sportTypeId}
                            isEditing={editing}
                        />
                    </>
                );
            case 4:
                return (
                    <AddSponsorTier
                        tiers={tiers}
                        setTiers={setTiers}
                        formApi={formApi}
                        sportTypeId={sportTypeId}
                        clubId={values.club}
                        remainingTiers={remainingTiers}
                        setRemainingTiers={setRemainingTiers}
                    />
                );

            case 5:
                return (
                    <HealthAndSafety
                        formApi={formApi}
                        sportTypeId={sportTypeId}
                    />
                );

            case 6:
                return (
                    <EventPayment
                        formApi={formApi}
                        paymentAccountIsSetup={clubDetails.paymentsEnabled}
                        verifiedAllowed={clubDetails ? clubDetails.sanctioned : false}
                        stripeUrl={stripeUrl}
                        activePaymentsEnabled={activePaymentsEnabled}
                        showPaymentSetup={showPaymentSetup}
                        onBack={onBack}
                        error={null}
                        isOwner={isOwner}
                    />
                );
            default:
                break;
        }

        return null;
    };

    return (
        <>
            {currentStep === 1 && (
                <>
                    {showSelectHSEventType() && (
                        <SelectHSEventType
                            field="hsEventType"
                            value={values.hsEventType}
                            formApi={formApi}
                            onChange={onHSEventTypeChange}
                            editing={editing}
                        />
                    )}
                    {!clubIsHighSchool && (
                        <>
                            <EventName
                                field="name"
                                formApi={formApi}
                                clubTierType={clubDetails.tierType}
                            />
                            <EventLocation
                                field="eventLocations"
                                formApi={formApi}
                                isEditing={editing}
                                isRegionLocked={isRegionLocked}
                                isHighSchoolClub={clubIsHighSchool}
                            />
                        </>
                    )}
                    {showSelectEventType() && (
                        <SelectEventType
                            field="eventType"
                            value={values.eventType}
                            formApi={formApi}
                            onChange={onEventTypeChange}
                            editing={editing}
                            sportTypeId={sportTypeId}
                        />
                    )}
                </>
            )}
            {getStepFields()}
        </>
    );
}

StepFields.propTypes = {
    formApi: PropTypes.shape({
        values: PropTypes.objectOf(PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
        ])),
        errors: PropTypes.objectOf(PropTypes.string),
        submitForm: PropTypes.func,
        setValue: PropTypes.func,
        setAllValues: PropTypes.func,
    }).isRequired,
    club: PropTypes.shape({
        clubDetails: clubShape,
    }).isRequired,
    defaultValues: PropTypes.shape({
        eventType: PropTypes.number,
        sportTypeId: PropTypes.number,
        name: PropTypes.string,
        eventDivisions: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            genderId: PropTypes.number,
            divisionFormatTypeId: PropTypes.number,
        })),
        eventLocations: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            address: PropTypes.string,
        })),
    }).isRequired,
    currentStep: PropTypes.number.isRequired,
    showTip: PropTypes.bool,
    showPaymentSetup: PropTypes.bool,
    stripeUrl: PropTypes.string.isRequired,
    activePaymentsEnabled: PropTypes.bool,
    isDemoEvent: PropTypes.bool,
    openModal: PropTypes.bool,
    closeCreatePage: PropTypes.func,
    applyForVerified: PropTypes.func,
    verificationApplied: PropTypes.bool,
    onBack: PropTypes.func,
    organizers: PropTypes.objectOf(Array),
    setOrganizers: PropTypes.func,
    onHSEventTypeChange: PropTypes.func,
    onEventTypeChange: PropTypes.func,
    onDivisionFeeChange: PropTypes.func,
    onVerifiedChange: PropTypes.func,
    PopUpTypes: PropTypes.objectOf(PropTypes.string).isRequired,
    editing: PropTypes.bool,
    tiers: PropTypes.shape({
        tiers: PropTypes.arrayOf(tierShape),
        nameInputsAreTouched: PropTypes.bool,
    }).isRequired,
    setTiers: PropTypes.func.isRequired,
    remainingTiers: PropTypes.instanceOf(Array).isRequired,
    setRemainingTiers: PropTypes.func.isRequired,
    isOwner: PropTypes.bool,
    showUpgradeToPerformanceHsPopup: PropTypes.bool,
    isRegionLocked: PropTypes.bool,
    eventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    isEventCreateForm: PropTypes.bool,
    sportTypeId: PropTypes.number,
    hasDraws: PropTypes.bool,
    registrationsCount: PropTypes.number,
};

StepFields.defaultProps = {
    showTip: false,
    showPaymentSetup: false,
    activePaymentsEnabled: false,
    isDemoEvent: false,
    openModal: false,
    closeCreatePage: () => { },
    applyForVerified: () => { },
    verificationApplied: false,
    onBack: () => { },
    organizers: PropTypes.array,
    setOrganizers: () => { },
    onHSEventTypeChange: () => { },
    onEventTypeChange: () => { },
    onDivisionFeeChange: () => { },
    onVerifiedChange: () => { },
    isRegionLocked: false,
    editing: false,
    isOwner: false,
    showUpgradeToPerformanceHsPopup: false,
    isEventCreateForm: false,
    sportTypeId: sportTypeIds.TENNIS,
    hasDraws: false,
    registrationsCount: 0,
};

export default StepFields;
